"use client"

import { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { getSettings, getPathOfStatic } from "../../utils"
import Timer from "../components/timer"
import { useLanguage } from "../../../../../contexts/LanguageContext"


const getRndInteger = (min, max) => {
  if (min === max) return min
  if (min > max) {
    const x = min
    min = max
    max = x
  }
  return Math.floor(Math.random() * (max - min + 1)) + min
}

class RandomWheel {
  constructor(arr) {
    this.arr = arr
  }

  next() {
    const i = getRndInteger(0, this.arr.length - 1)
    const y = this.arr.splice(i, 1)[0]
    if (this.x != null) {
      this.arr.push(this.x)
    }
    this.x = y
    return y
  }
}

const getDivPairs = (d1Min, d1Max, d2Min, d2Max, multiMax) => {
  const result = []
  for (let i = d1Min; i < d1Max; i++) {
    for (let j = d2Min; j < d2Max; j++) {
      if (i * j >= multiMax) {
        break
      }
      result.push([i * j, j])
    }
  }
  return result
}

const divPairs = [
  new RandomWheel(getDivPairs(2, 10, 10, 50, 100)),
  new RandomWheel(getDivPairs(10, 99, 10, 99, 1000)),
  new RandomWheel(getDivPairs(100, 999, 10, 99, 10000)),
  new RandomWheel(getDivPairs(10, 99, 100, 999, 10000)),
]

const getExample = (theme, difficulties, num) => {
  if (theme === 0) {
    // multiplication
    let a = 0
    let b = 0
    switch (difficulties[theme]) {
      case 0: {
        a = getRndInteger(1, 9)
        b = num === 8 ? getRndInteger(1, 9) : num + 2
        break
      }
      case 1: {
        a = getRndInteger(10, 99)
        b = num === 8 ? getRndInteger(1, 9) : num + 2
        break
      }
      case 2: {
        a = getRndInteger(100, 999)
        b = num === 8 ? getRndInteger(1, 9) : num + 2
        break
      }
      case 3: {
        a = getRndInteger(10, 99)
        b =
          num < 9
            ? num + 11
            : num < 17
              ? getRndInteger(20 + (num - 9) * 10, 29 + (num - 9) * 10)
              : getRndInteger(10, 99)
        break
      }
      case 4: {
        a = getRndInteger(1000, 9999)
        b = num === 8 ? getRndInteger(1, 9) : num + 2
        break
      }
      case 5: {
        a = getRndInteger(100, 999)
        b = getRndInteger(10, 99)
        break
      }
      case 6: {
        switch (num) {
          case 0:
            a = getRndInteger(50, 60)
            b = getRndInteger(50, 60)
            break
          case 1:
            a = getRndInteger(40, 50)
            b = getRndInteger(40, 50)
            break
          case 2:
            a = getRndInteger(40, 50)
            b = getRndInteger(50, 60)
            break
          default:
        }
        break
      }
      case 7: {
        switch (num) {
          case 0:
            a = getRndInteger(100, 110)
            b = getRndInteger(100, 110)
            break
          case 1:
            a = getRndInteger(90, 100)
            b = getRndInteger(90, 100)
            break
          case 2:
            a = getRndInteger(90, 100)
            b = getRndInteger(100, 110)
            break
          default:
        }
        break
      }
      case 8: {
        switch (num) {
          case 0:
            a = getRndInteger(190, 200)
            b = getRndInteger(190, 200)
            break
          case 1:
            a = getRndInteger(200, 210)
            b = getRndInteger(200, 210)
            break
          case 2:
            a = getRndInteger(190, 200)
            b = getRndInteger(200, 210)
            break
          default:
        }
        break
      }
      default:
    }
    return [`${a} x ${b}`, a * b]
  } else if (theme === 1) {
    let b = 0
    if (difficulties[theme] <= 3) {
      b = num === 8 ? getRndInteger(1, 9) : num + 2
    }
    let a = 0
    switch (difficulties[theme]) {
      case 0: {
        a = b * getRndInteger(2, 9)
        break
      }
      case 1: {
        const max = Math.floor(99 / (num + 2))
        const min = Math.floor(10 / (num + 2))
        a = b * getRndInteger(min, max)
        break
      }
      case 2: {
        const max = Math.floor(999 / (num + 2))
        const min = Math.floor(100 / (num + 2))
        a = b * getRndInteger(min, max)
        break
      }
      case 3: {
        const max = Math.floor(9999 / (num + 2))
        const min = Math.floor(1000 / (num + 2))
        a = b * getRndInteger(min, max)
        break
      }
      case 4:
      case 5:
      case 6:
      case 7: {
        const pair = divPairs[difficulties[theme] - 4].next()
        a = pair[0]
        b = pair[1]
        break
      }
      default:
    }
    return [`${a} / ${b}`, a / b]
  } else if (theme === 2) {
    let a = 0
    switch (difficulties[theme]) {
      case 0:
        a = getRndInteger(2, 10)
        break
      case 1:
        a = getRndInteger(2, 9) * 10 + 5
        break
      case 2:
        a = getRndInteger(11, 19)
        break
      case 3:
        a = getRndInteger(20, 30)
        break
      case 4:
        a = getRndInteger(30, 70)
        break
      case 5:
        a = getRndInteger(80, 120)
        break
      case 6:
        a = getRndInteger(10, 99)
        break
      default:
    }
    return [`${a}²`, a * a]
  }
}

const Training = ({ onFinish, onNext, player }) => {
  const { t } = useLanguage()
  const [showResult, setShowResult] = useState(false)
  const [success, setSuccess] = useState(false)
  const [example, setExample] = useState("")
  const [result, setResult] = useState(0)

  const timerRef = useRef(null)
  const settingsRef = useRef(null)
  const answerRef = useRef(null)

  useEffect(() => {
    settingsRef.current = getSettings("multi")
    const [newExample, newResult] = getExample(
      settingsRef.current.theme,
      settingsRef.current.difficulties,
      settingsRef.current.num,
    )
    setExample(newExample)
    setResult(newResult)

    timerRef.current = setTimeout(() => {
      checkResult()
    }, settingsRef.current.time * 1000)

    return () => {
      clearTimeout(timerRef.current)
    }
  }, [])

  const checkResult = (player) => {
    clearTimeout(timerRef.current);
    const answer = answerRef.current?.value;
    const isSuccess = Number(answer) === Number(result);
    setShowResult(true);
    setSuccess(isSuccess);

    player.src = getPathOfStatic("/sounds/" + (!isSuccess ? "no" : "") + "success.mp3");
    player.volume = 1
    try {
      player.play()
    } catch (e) {
      // Handle audio error
    }
  }

  const handleKeyDown = (event) => {
    if (event.keyCode === 13) {
      checkResult(player)
    }
  }

  return (
    <div>
      <h2>{t("performExercise")}</h2>
      <section>
        {showResult ? (
          <div>
            <div className="answerIcon">
              <img
                alt=""
                src={getPathOfStatic(`/img/smiles/${success ? "cool" : "hm"}.png`)}
                width="400"
                height="266"
              />
            </div>
            <div className={`${success ? "" : "no"}success`}>{success ? t("correct") : t("incorrect")}</div>
            <div className="success">
              {t("answer")}: {result}
            </div>
          </div>
        ) : (
          <div className="answer">
            <div>{example}</div>
            <div>{t("yourAnswer")}</div>
            <div>
              <input ref={answerRef} className="answer" autoFocus={true} type="number" onKeyDown={handleKeyDown} />
            </div>
            <Timer time={settingsRef?.current?.time} />
          </div>
        )}
      </section>
      <footer>
        <button onClick={() => onFinish()}>
          <div>{t("settings")}</div>
        </button>
        {showResult && (
          <button onClick={() => onNext()}>
            <div>{t("continue")}</div>
          </button>
        )}
      </footer>
    </div>
  )
}

Training.propTypes = {
  onFinish: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
}

export default Training
